import { Link, useLocation } from "react-router-dom";
import { Bell, Search, Menu, ChevronRight, Sun, Moon } from "lucide-react";
import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LanguageSwitcher } from "@/components/site/LanguageSwitcher";
import { useI18n } from "@/i18n";

export function AdminTopbar({ onMobileMenu }: { onMobileMenu: () => void }) {
  const { pathname } = useLocation();
  const { t } = useI18n();
  const segments = pathname.split("/").filter(Boolean);
  const [isDark, setIsDark] = useState(true);

  const crumbLabels: Record<string, string> = {
    admin: t.admin.crumbs.admin,
    vehicles: t.admin.crumbs.vehicles,
    leads: t.admin.crumbs.leads,
    media: t.admin.crumbs.media,
    seo: t.admin.crumbs.seo,
    analytics: t.admin.crumbs.analytics,
    settings: t.admin.crumbs.settings,
    new: t.admin.crumbs.new,
  };

  useEffect(() => {
    document.documentElement.classList.toggle("dark", isDark);
  }, [isDark]);

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-border/60 bg-background/70 px-4 backdrop-blur-xl sm:px-6">
      <button
        onClick={onMobileMenu}
        aria-label={t.admin.menu}
        className="grid h-9 w-9 place-items-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground lg:hidden"
      >
        <Menu className="h-4 w-4" />
      </button>

      <nav className="hidden min-w-0 flex-1 items-center gap-1.5 text-sm text-muted-foreground md:flex">
        {segments.map((seg, i) => {
          const href = "/" + segments.slice(0, i + 1).join("/");
          const isLast = i === segments.length - 1;
          const label = crumbLabels[seg] ?? seg;
          return (
            <div key={href} className="flex items-center gap-1.5">
              {i > 0 && <ChevronRight className="h-3.5 w-3.5 opacity-50" />}
              {isLast ? (
                <span className="truncate font-medium text-foreground">{label}</span>
              ) : (
                <Link to={href} className="truncate hover:text-foreground">
                  {label}
                </Link>
              )}
            </div>
          );
        })}
      </nav>

      <div className="relative ml-auto hidden max-w-md flex-1 md:block">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder={t.admin.searchPlaceholder}
          className="h-9 rounded-full border-border/70 bg-surface/70 pl-9 pr-16 text-sm shadow-inner"
        />
        <kbd className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 rounded border border-border/60 bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
          ⌘K
        </kbd>
      </div>

      <LanguageSwitcher />

      <button
        onClick={() => setIsDark((d) => !d)}
        aria-label={t.admin.toggleTheme}
        className="grid h-9 w-9 place-items-center rounded-md text-muted-foreground transition hover:bg-muted hover:text-foreground"
      >
        {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
      </button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            aria-label={t.admin.recentActivity}
            className="relative grid h-9 w-9 place-items-center rounded-md text-muted-foreground transition hover:bg-muted hover:text-foreground"
          >
            <Bell className="h-4 w-4" />
            <span className="absolute right-2 top-2 h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_8px_var(--primary)]" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-80">
          <DropdownMenuLabel>{t.admin.recentActivity}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {t.admin.notifications.map((n) => (
            <DropdownMenuItem key={n} className="flex flex-col items-start gap-0.5">
              <span className="text-sm">{n}</span>
              <span className="text-[10px] text-muted-foreground">{t.admin.minutesAgo}</span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-2.5 rounded-full border border-border/60 bg-surface/70 py-1 pl-1 pr-3 text-left transition hover:bg-muted">
            <div className="grid h-7 w-7 place-items-center rounded-full bg-gradient-to-br from-primary to-primary/40 font-display text-xs font-semibold text-primary-foreground">
              AM
            </div>
            <div className="hidden leading-tight sm:block">
              <div className="text-xs font-semibold">Ava Marchetti</div>
              <div className="text-[10px] text-muted-foreground">{t.admin.roleSalesDirector}</div>
            </div>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          <DropdownMenuLabel>{t.admin.myAccount}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem>{t.admin.profile}</DropdownMenuItem>
          <DropdownMenuItem>{t.admin.preferences}</DropdownMenuItem>
          <DropdownMenuItem>{t.admin.billing}</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link to="/">{t.admin.returnToSite}</Link>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
